import Vue from "vue";
import VueRouter from "vue-router";
import Dashboard from "../views/Dashboard.vue";
import { authGuard } from "../auth/authGuard";
import store from "../store";
import { setReportingCompany } from "@/helpers/local";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "dashboard",
    meta: { title: "Dashboard", allowMembers: true },
    component: Dashboard
  },
  {
    path: "/signup",
    name: "signup",
    meta: { title: "Signup" },
    component: Dashboard
  },
  {
    path: "/slack",
    name: "slack",
    meta: { title: "Slack Integration" },
    component: () =>
      import(
        /* webpackChunkName: "integrations" */ "../views/integrations/Slack.vue"
      )
  },
  {
    path: "/profile",
    name: "profile",
    meta: { title: "Profile", allowMembers: true, isDetail: true },
    component: () =>
      import(
        /* webpackChunkName: "userProfile" */ "../views/profile/UserProfile.vue"
      )
  },
  {
    path: "/profile/:tab",
    name: "profileWithTabs",
    meta: { title: "Profile", allowMembers: true, isDetail: true },
    component: () =>
      import(
        /* webpackChunkName: "userProfile" */ "../views/profile/UserProfile.vue"
      )
  },
  {
    path: "/company",
    name: "companyProfile",
    meta: { title: "Company", isDetail: true },
    component: () =>
      import(
        /* webpackChunkName: "company" */ "../views/company/CompanyProfile.vue"
      )
  },
  {
    path: "/company/:tab",
    name: "companyProfileWithTabs",
    meta: { title: "Company", isDetail: true },
    component: () =>
      import(
        /* webpackChunkName: "company" */ "../views/company/CompanyProfile.vue"
      )
  },
  {
    path: "/company/:tab/:id",
    name: "companyProfileWithTabsAndId",
    meta: { title: "Company", isDetail: true },
    component: () =>
      import(
        /* webpackChunkName: "company" */ "../views/company/CompanyProfile.vue"
      )
  },
  {
    path: "/team",
    redirect: "/team/members"
  },
  {
    path: "/team/:tab",
    name: "teamProfileWithTabs",
    meta: { title: "Team", isDetail: true },
    component: () =>
      import(
        /* webpackChunkName: "teams" */ "../views/members/ManagerDetails.vue"
      )
  },
  {
    path: "/core-values/add",
    name: "coreValuesAdd",
    meta: {
      title: "Add Core Value",
      breadcrumbTitle: "Add",
      breadcrumbs: [{ text: "Core Values", to: "/company" }],
      onlyAdmin: true
    },
    component: () =>
      import(
        /* webpackChunkName: "coreValues" */ "../views/company/AddCoreValue.vue"
      )
  },
  {
    path: "/core-values/:id",
    name: "coreValueDetail",
    props: true,
    meta: {
      title: "Core Value Detail",
      breadcrumbTitle: "Detail",
      breadcrumbs: [{ text: "Core Values", to: "/company" }],
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "coreValues" */ "../views/company/CoreValueDetails.vue"
      )
  },
  {
    path: "/core-values/:id/:tab",
    name: "coreValueDetailWithTab",
    props: true,
    meta: {
      title: "Core Value Detail",
      breadcrumbTitle: "Detail",
      breadcrumbs: [{ text: "Core Values", to: "/company" }],
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "coreValues" */ "../views/company/CoreValueDetails.vue"
      )
  },
  {
    path: "/mission",
    name: "mission",
    meta: {
      title: "Mission",
      breadcrumbTitle: "Mission",
      breadcrumbs: [{ text: "Company", to: "/company" }],
      isDetail: true
    },
    component: () =>
      import(/* webpackChunkName: "mission" */ "../views/company/Mission.vue")
  },
  {
    path: "/mission/:tab",
    name: "missionWithTab",
    meta: {
      title: "Mission",
      breadcrumbTitle: "Mission",
      breadcrumbs: [{ text: "Company", to: "/company" }],
      isDetail: true
    },
    component: () =>
      import(/* webpackChunkName: "mission" */ "../views/company/Mission.vue")
  },
  {
    path: "/strategic-anchors",
    name: "strategicAnchors",
    meta: {
      title: "Strategic Anchors",
      breadcrumbTitle: "Strategic Anchors",
      breadcrumbs: [{ text: "Company", to: "/company" }],
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "strategicAnchors" */ "../views/company/StrategicAnchors.vue"
      )
  },
  {
    path: "/members/:id",
    name: "memberDetails",
    meta: {
      title: "Member Detail",
      breadcrumbTitle: "Detail",
      breadcrumbs: [
        {
          text: "Members",
          to: "/company/members"
        }
      ],
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "members" */ "../views/members/MemberDetails.vue"
      )
  },
  {
    path: "/members/:id/:tab",
    name: "memberDetailsWithTab",
    meta: {
      title: "Member Detail",
      breadcrumbTitle: "Detail",
      breadcrumbs: [{ text: "Members", to: "/company/members" }],
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "members" */ "../views/members/MemberDetails.vue"
      )
  },
  {
    path: "/departments/:id",
    name: "teamDetails",
    meta: {
      title: "Department Detail",
      breadcrumbTitle: "Detail",
      breadcrumbs: [{ text: "Departments", to: "/company/departments" }],
      isDetail: true
    },
    component: () =>
      import(/* webpackChunkName: "teams" */ "../views/teams/TeamDetails.vue")
  },
  {
    path: "/departments/:id/:tab",
    name: "teamDetailsWithTab",
    meta: {
      title: "Department Detail",
      breadcrumbTitle: "Detail",
      breadcrumbs: [{ text: "Departments", to: "/company/departments" }],
      isDetail: true
    },
    component: () =>
      import(/* webpackChunkName: "teams" */ "../views/teams/TeamDetails.vue")
  },
  {
    path: "/features/:tab",
    name: "featuresWithTabs",
    meta: {
      title: "Feature Hub",
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/features/FeatureDashboard.vue"
      )
  },
  {
    path: "/features/:feature/settings",
    name: "featureSettings",
    meta: {
      title: "Settings",
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/settings/Settings.vue"
      )
  },
  {
    path: "/features/:feature/settings/:id",
    name: "featureSettingsDetail",
    meta: {
      title: "Settings",
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/settings/Settings.vue"
      )
  },
  {
    path: "/features/surveys/create",
    name: "createSurvey",
    meta: {
      title: "Create Survey",
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/surveys/CreateSurvey.vue"
      )
  },
  {
    path: "/features/surveys/:id/builder",
    name: "surveyBuilder",
    meta: {
      title: "Survey Builder",
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/surveys/SurveyBuilder.vue"
      )
  },
  {
    path: "/features/surveys/:id/:instanceId/live",
    name: "liveSurvey",
    meta: {
      title: "Live Survey",
      hideDefaultNavigation: true,
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/surveys/LiveSurvey.vue"
      )
  },
  {
    path: "/features/:tab/:id/report",
    name: "surveyDetailsWithTab",
    meta: {
      title: "Report",
      breadcrumbTitle: "Report",
      breadcrumbs: [{ text: "Surveys", to: "/features/surveys", exact: true }],
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "features" */ "../views/features/FeatureDashboard.vue"
      )
  },
  {
    path: "/billing",
    name: "billing",
    meta: {
      title: "Billing"
    },
    component: () =>
      import(/* webpackChunkName: "billing" */ "../views/billing/Billing.vue")
  },
  {
    path: "/meetings/:definitionId",
    name: "checkinsDetail",
    meta: {
      title: "Meeting Details",
      hideDefaultNavigation: true,
      isCheckin: true,
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/checkin/CheckInDetails.vue"
      )
  },
  {
    path: "/meetings/:definitionId/start",
    name: "checkinsDetailStart",
    meta: {
      title: "Meeting Details",
      hideDefaultNavigation: true,
      isCheckin: true,
      isStart: true,
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/checkin/CheckInDetails.vue"
      )
  },
  {
    path: "/meetings/:definitionId/:id",
    name: "checkinsDetailHistory",
    meta: {
      title: "Meeting Details",
      hideDefaultNavigation: true,
      isCheckin: true,
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/checkin/CheckInDetails.vue"
      )
  },
  {
    path: "/features/reviews/settings/questions/preview",
    name: "reviewDetailsQuestionsPreview",
    meta: {
      title: "Review",
      isDetail: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/reviews/ReviewPreview.vue"
      )
  },
  {
    path: "/annual-reviews/:userId",
    name: "annualReviewUserOverview",
    meta: {
      title: "Annual Review Overview",
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/annualreviews/AnnualReviewUserOverview.vue"
      )
  },
  {
    path: "/annual-reviews/:userId/:id",
    name: "annualReviewDetail",
    meta: {
      title: "Annual Review Detail",
      hideDefaultNavigation: true,
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/annualreviews/AnnualReviewDetails.vue"
      )
  },
  {
    path: "/annual-reviews/:userId/:id/:tab",
    name: "annualReviewDetailTab",
    meta: {
      title: "Annual Review Detail",
      hideDefaultNavigation: true,
      allowMembers: true
    },
    component: () =>
      import(
        /* webpackChunkName: "performance" */ "../views/annualreviews/AnnualReviewDetails.vue"
      )
  },
  {
    path: "/features",
    name: "Features",
    meta: {
      title: "Features",
      isDetail: true
    },
    component: () =>
      import(
        /* webpackChunkName: "settings" */ "../views/settings/FeatureManagement.vue"
      )
  },
  {
    path: "/tasks",
    name: "tasks",
    meta: {
      title: "Task Hub",
      isDetail: true,
      allowMembers: true
    },
    component: () =>
      import(/* webpackChunkName: "tasks" */ "../views/tasks/TaskDashboard.vue")
  },
  {
    path: "/tasks/:id",
    name: "tasksWithId",
    meta: {
      title: "Task Hub",
      isDetail: true,
      allowMembers: true
    },
    component: () =>
      import(/* webpackChunkName: "tasks" */ "../views/tasks/TaskDashboard.vue")
  },
  {
    path: "/reporting",
    name: "reportingHub",
    meta: {
      title: "Reporting Hub",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingHub.vue"
      )
  },
  {
    path: "/reporting/:filterId",
    name: "reportingHubWithFilter",
    meta: {
      title: "Reporting Hub",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingHub.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId",
    name: "reportingDetails",
    meta: {
      title: "Reporting Details",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/surveys/:surveyId",
    name: "surveyReportingDetails",
    meta: {
      title: "Survey Report",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path:
      "/reporting/:parentId/:reportId/surveys/:surveyId/sends/:surveyInstanceId",
    name: "surveyReportingDetailsWithInstance",
    meta: {
      title: "Survey Report",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path:
      "/reporting/:parentId/:reportId/surveys/:surveyId/sends/:surveyInstanceId/members/:userId",
    name: "surveyReportingDetailsWithInstanceMember",
    meta: {
      title: "Survey Report",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/training/:resourceId",
    name: "resourceReportingDetails",
    meta: {
      title: "Training Report",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path:
      "/reporting/:parentId/:reportId/training/:resourceId/sends/:resourceScheduleId",
    name: "resourceReportingDetailsWithInstance",
    meta: {
      title: "Training Report",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/:type/",
    name: "reportingDetailsType",
    meta: {
      title: "Reporting Details",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/:type/:typeId",
    name: "reportingDetailsTypeWithId",
    meta: {
      title: "Reporting Details",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/:type/:typeId/categories/:categoryId",
    name: "reportingDetailsMemberCategory",
    meta: {
      title: "Reporting Details",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/:type/:typeId/questions/:questionId",
    name: "reportingDetailsMemberQuestions",
    meta: {
      title: "Reporting Details",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/reporting/:parentId/:reportId/:type/:typeId/:tab",
    name: "reportingDetailsTypeWithIdAndTab",
    meta: {
      title: "Reporting Details",
      allowMembers: true,
      hideDefaultNavigation: true
    },
    component: () =>
      import(
        /* webpackChunkName: "reporting" */ "../views/reporting/ReportingDetails.vue"
      )
  },
  {
    path: "/portal",
    name: "portal",
    meta: {
      title: "CorePortal"
    },
    component: () =>
      import(/* webpackChunkName: "portal" */ "../views/portal/CorePortal.vue")
  },
  {
    path: "/portal/:tab",
    name: "portalWithTabs",
    meta: {
      title: "CorePortal"
    },
    component: () =>
      import(/* webpackChunkName: "portal" */ "../views/portal/CorePortal.vue")
  },
  {
    path: "/portal/customers/add",
    name: "portalAddCustomer",
    meta: { isSetup: true, title: "Add Customer" },
    component: () =>
      import(
        /* webpackChunkName: "portal" */ "../views/portal/CreateCustomer.vue"
      )
  },
  {
    path: "/portal/:tab/:id",
    name: "portalWithTabsAndId",
    meta: {
      title: "CorePortal"
    },
    component: () =>
      import(/* webpackChunkName: "portal" */ "../views/portal/CorePortal.vue")
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});

router.beforeEach((to, from, next) => {
  if (to.meta) {
    document.title = to.meta.title + " | CoreCounts";
  }
  if (
    to.path.indexOf("/reporting") == 0 &&
    from.path.indexOf("/reporting") < 0
  ) {
    store.dispatch("reporting/setReturnUrl", from.path);
  } else if (
    to.path.indexOf("/reporting") < 0 &&
    from.path.indexOf("/reporting") == 0
  ) {
    setReportingCompany("");
  }
  authGuard(to, from, next);
  //next();
});

export default router;
